/**
 * Establishment — the organization that occupies a generated building, and the
 * seed all generation derives from. Budget ($–$$$$$ = 1..5) selects a
 * `BuildProfile` (from `CONFIG.gen.budget`) that scales the building's size and
 * density. Headless + deterministic (rule 1/4): everything rolls from the seed
 * via `mulberry32`/`hashSeed` (src/sim/rng.ts).
 */

import { CONFIG } from '../config';
import { hashSeed, mulberry32 } from './rng';

export type Budget = 1 | 2 | 3 | 4 | 5;
export type BuildingTitle = 'corporate-office';
/** Building operating state (time-of-day). Keys into `CONFIG.operating`. */
export type OperatingState = 'in-use' | 'off-duty';

export interface Establishment {
  name: string;
  title: BuildingTitle;
  budget: Budget;
  seed: number;
}

/** The budget-scaled build profile (floor size/count, densities, security). */
export type BuildProfile = (typeof CONFIG.gen.budget)[number];

/** Look up the BuildProfile for a budget level (1..5). */
export function buildProfile(budget: Budget): BuildProfile {
  return CONFIG.gen.budget[budget - 1]!;
}

const NAME_PREFIX = [
  'Volkov', 'Northwind', 'Meridian', 'Apex', 'Halcyon', 'Cygnus', 'Orion',
  'Sterling', 'Kessler', 'Aldwych', 'Vantage', 'Chagat', 'Marlowe', 'Ridgeway',
] as const;
const NAME_SUFFIX = [
  'Corp', 'Holdings', 'Industries', 'Group', 'LLP', 'Systems', 'Partners',
  'Dynamics', 'Consolidated', 'Global', 'Trust', 'Associates',
] as const;

function pick<T>(arr: readonly T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)]!;
}

/** Roll an establishment from a seed. `budget`/`title` override the random roll
 *  (the mission screen supplies them; leaving them out rolls one). */
export function rollEstablishment(
  seed: number,
  opts: { budget?: Budget; title?: BuildingTitle; name?: string } = {},
): Establishment {
  const rng = mulberry32(hashSeed(seed, 0xe57));
  const name = opts.name ?? `${pick(NAME_PREFIX, rng)} ${pick(NAME_SUFFIX, rng)}`;
  const budget = opts.budget ?? ((1 + Math.floor(rng() * 5)) as Budget);
  const title = opts.title ?? 'corporate-office';
  return { name, title, budget, seed };
}
